<template>
  <hr-adaptive-menu
    ref="menuRef"
    v-slot="{ isMobile }"
    content="freeform"
    :model-value="modelValue"
    :title="$t('editor.table.insertTable')"
    :auto-close="false"
    anchor="top end"
    self="top start"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div
      class="table-picker"
      :class="{ 'table-picker--mobile': isMobileDevice }"
    >
      <div class="table-picker__toggle">
        <hr-setting-switch
          v-model="isHeaderEnable"
          :label="$t('editor.table.addHeaderRow')"
        />
      </div>
      <div :class="{ 'disable-table-menu': isTableActive }">
        <hr-tooltip
          v-if="isTableActive && !isMobileDevice"
        >
          {{ $t('editor.table.forbiddenInsertTable') }}
        </hr-tooltip>
        <p
          v-else-if="isTableActive"
          class="disable-table-menu__mobile-text"
        >
          {{ $t('editor.table.forbiddenInsertTable') }}
        </p>
        <div
          v-for="(row) in tableRows"
          :key="row"
          class="row"
          :class="{'flex-center': isMobileDevice}"
        >
          <div
            v-for="(btn) in tableCols"
            :key="`${row}-${btn}`"
            class="button__wrapper"
            @mouseover="setHoveredButton(row, btn)"
            @mouseout="setHoveredButton(0, 0)"
            @click="isMobileDevice ? setTableSize(row, btn) : insertTable()"
          >
            <div
              class="table-picker-cell"
              :class="{ 'table-picker-cell--active': isButtonPainted(row, btn) }"
            />
          </div>
        </div>
        <div
          v-if="!isMobile"
          class="full-width text-center"
        >
          {{ hoveredButton.row }}x{{ hoveredButton.col }}
        </div>
      </div>
      <div
        v-if="isMobile"
        class="row"
      >
        <div class="col row justify-center">
          <span class="hr-label-medium full-width text-center">
            {{ $t('editor.table.rows') }}
          </span>
          <hr-icon-action-btn
            icon="remove"
            :disable="isTableActive"
            @click="decrementRows"
            @dblclick.prevent
          />
          <span class="hr-body-large q-mx-md q-my-auto">
            {{ hoveredButton.row }}
          </span>
          <hr-icon-action-btn
            icon="add"
            :disable="isTableActive"
            @click="incrementRows"
            @dblclick.prevent
          />
        </div>
        <div class="col row justify-center">
          <span class="hr-label-medium full-width text-center">
            {{ $t('editor.table.columns') }}
          </span>
          <hr-icon-action-btn
            icon="remove"
            :disable="isTableActive"
            @click="decrementCols"
            @dblclick.prevent
          />
          <span class="hr-body-large q-mx-md q-my-auto">
            {{ hoveredButton.col }}
          </span>
          <hr-icon-action-btn
            icon="add"
            :disable="isTableActive"
            @click="incrementCols"
            @dblclick.prevent
          />
        </div>
      </div>
      <div v-if="isMobile">
        <hr-confirm-btn
          :label="$t('editor.table.insertTable')"
          :disable="!isSatisfyingTableSizes || isTableActive"
          class="full-width"
          @click="insertTable"
        />
      </div>
    </div>
  </hr-adaptive-menu>
</template>

<script>
import { Editor } from '@tiptap/vue-3';
import { HrAdaptiveMenu, HrConfirmBtn, HrIconActionBtn, HrSettingSwitch, HrTooltip } from 'src/design-system';

export default {
  name: 'TextEditorInsertTable',

  components: {
    HrAdaptiveMenu,
    HrConfirmBtn,
    HrIconActionBtn,
    HrSettingSwitch,
    HrTooltip,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    editor: {
      type: Editor,
      required: true,
    },

    defaultTableRows: {
      type: Number,
      default: 6,
    },
    defaultTableCols: {
      type: Number,
      default: 6,
    },
  },

  emits: ['update:modelValue', 'close'],

  data() {
    return {
      hoveredButton: {
        row: 0,
        col: 0,
      },
      isHeaderEnable: true,

      tableRows: this.defaultTableRows,
      tableCols: this.defaultTableCols,

      maxTableCols: 10,
      maxTableRows: 10,
    };
  },

  computed: {
    isMobileDevice() {
      return this.$q.platform.is.mobile || this.$q.screen.lt.sm;
    },

    isSatisfyingTableSizes() {
      return (this.hoveredButton.col > 0 && this.hoveredButton.row > 0);
    },

    isTableActive() {
      return this.editor.isActive('table');
    },
  },

  watch: {
    hoveredButton: {
      deep: true,
      handler({ row, col }) {
        if (!col || !row) {
          return;
        }

        // reset sizes to default if hovered button is within default table borders
        if (row < this.defaultTableRows) {
          this.tableRows = this.defaultTableRows;
        }
        if (col < this.defaultTableRows) {
          this.tableCols = this.defaultTableRows;
        }

        this.expandTableInsertMenu(row, col);
      },
    },
  },

  methods: {
    /**
     * @param {number} btnRow
     * @param {number} btnCol
     */
    isButtonPainted(btnRow, btnCol) {
      return ((btnRow <= this.hoveredButton.row) && (btnCol <= this.hoveredButton.col));
    },

    /**
     * @param {number | null} [row]
     * @param {number | null} [col]
     */
    setHoveredButton(row, col) {
      if (this.isMobileDevice) {
        return;
      }

      this.hoveredButton = { row, col };
    },

    /**
     * @param {number} hoveredRow
     * @param {number} hoveredCol
     */
    expandTableInsertMenu(hoveredRow, hoveredCol) {
      if (hoveredRow === this.tableRows && hoveredRow < this.maxTableRows) {
        this.tableRows += 1;
      } else if ((hoveredRow < this.tableRows && (hoveredRow + 1) >= this.defaultTableRows)
        || hoveredRow > this.tableRows) {
        this.tableRows = hoveredRow + 1;
      }

      if (hoveredCol === this.tableCols && hoveredCol < this.maxTableCols) {
        this.tableCols += 1;
      } else if ((hoveredCol < this.tableCols && (hoveredCol + 1) >= this.defaultTableCols)
        || hoveredCol > this.tableCols) {
        this.tableCols = hoveredCol + 1;
      }
    },

    setTableSize(row, col) {
      this.hoveredButton.row = row;
      this.hoveredButton.col = col;
    },

    insertTable() {
      if (this.isTableActive) {
        return;
      }

      this.editor.commands.insertTable({
        rows: this.hoveredButton.row,
        cols: this.hoveredButton.col,
        withHeaderRow: this.isHeaderEnable,
      });

      this.$refs.menuRef.hide();
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },

    incrementRows() {
      if (this.hoveredButton.row < this.maxTableRows || !this.hoveredButton.row) {
        this.hoveredButton.row += 1;
      }
    },

    decrementRows() {
      if (this.hoveredButton.row > 1) {
        this.hoveredButton.row -= 1;
      }
    },

    incrementCols() {
      if (this.hoveredButton.col < this.maxTableCols || !this.hoveredButton.col) {
        this.hoveredButton.col += 1;
      }
    },

    decrementCols() {
      if (this.hoveredButton.col > 1) {
        this.hoveredButton.col -= 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.table-picker {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  &--mobile {
    align-items: stretch;
  }

  &__toggle {
    align-self: stretch;
  }
}

[data-theme] .table-picker {
  color: var(--app-on-surface);
}

.button__wrapper {
  padding: 2px;
}

.table-picker-cell {
  width: 20px;
  height: 20px;
  border-radius: 2px;
  background: var(--app-surface-variant, #e0e0e0);

  &--active {
    background: var(--app-primary);
  }
}

.disable-table-menu {
  position: relative;
  z-index: 1;

  &::after {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 2;

    cursor: not-allowed;

    backdrop-filter: blur(3px);
    background-color: rgba(white, 0.1);
  }

  &__mobile-text {
    position: absolute;
    top: 50%;
    left: 0;
    z-index: 3;
    width: 100%;
    transform: translateY(-50%);

    font-size: 16px;
    text-align: center;
    font-weight: 700;
  }
}
</style>
